<template>
  <div
    class="align-center bg- flex !h-screen !w-screen items-center justify-center bg-[linear-gradient(33deg,rgba(26,174,232,1)_0%,rgba(109,197,232,1)_50%,rgba(207,240,255,1)_100%)] dark:[background-image:linear-gradient(33deg,#0f172a_0%,#1e293b_50%,#0f172a_100%)]"
  >
    <div
      class="card-container h-fit w-11/12 rounded-xl bg-[rgba(255,255,255,0.5)] p-8 shadow backdrop-blur-md dark:bg-slate-900/85 sm:w-10/12 md:w-[420px] md:p-12"
    >
      <div class="mb-4 flex items-center justify-center gap-4">
        <img src="@/assets/open-mdx.png" alt="logo openmdx" class="h-18 w-auto" />
      </div>

      <div class="text-sky-900 dark:text-slate-50">
        <Transition name="form-field" appear>
          <div class="mb-4" style="--delay: 0.1s">
            <p class="text-center text-sm font-medium text-[var(--p-primary-color)]">
              {{ languageStore.t('auth.requiredChangePassword.instructions') }}
            </p>
          </div>
        </Transition>

        <Transition name="form-field" appear>
          <div class="mb-4 flex flex-col gap-2" style="--delay: 0.2s">
            <label class="font-bold">
              {{ languageStore.t('auth.requiredChangePassword.emailLabel') }}
            </label>
            <InputText
              id="email"
              type="text"
              :placeholder="languageStore.t('auth.requiredChangePassword.placeholders.email')"
              v-model="form.email"
              :class="{ 'p-invalid': errors.email }"
              class="w-full"
            />
            <small v-if="errors.email" class="text-red-500">{{ errors.email }}</small>
          </div>
        </Transition>

        <Transition name="form-field" appear>
          <div class="mb-4 flex flex-col gap-2" style="--delay: 0.3s">
            <label class="font-bold">
              {{ languageStore.t('auth.requiredChangePassword.expiredPasswordLabel') }}
            </label>
            <Password
              toggleMask
              fluid
              v-model="form.expiredPassword"
              :invalid="!!errors.expiredPassword"
              :placeholder="
                languageStore.t('auth.requiredChangePassword.placeholders.expiredPassword')
              "
            />
            <small v-if="errors.expiredPassword" class="text-red-500">
              {{ errors.expiredPassword }}
            </small>
          </div>
        </Transition>

        <Transition name="form-field" appear>
          <div class="mb-4 flex flex-col gap-2" style="--delay: 0.4s">
            <label class="font-bold">
              {{ languageStore.t('auth.requiredChangePassword.newPasswordLabel') }}
            </label>
            <Password
              toggleMask
              fluid
              v-model="form.newPassword"
              :invalid="!!errors.newPassword"
              :placeholder="languageStore.t('auth.requiredChangePassword.placeholders.newPassword')"
            >
              <template #footer>
                <div class="mt-2 space-y-1">
                  <div class="flex items-center gap-2 text-xs">
                    <IconsCheck :isValid="passwordValidation.hasUppercase" :size="16" />
                    <span class="text-sm text-gray-600">
                      {{ languageStore.t('auth.requiredChangePassword.validation.uppercase') }}
                    </span>
                  </div>
                  <div class="flex items-center gap-2 text-xs">
                    <IconsCheck :isValid="passwordValidation.hasLowercase" :size="16" />
                    <span class="text-sm text-gray-600">
                      {{ languageStore.t('auth.requiredChangePassword.validation.lowercase') }}
                    </span>
                  </div>
                  <div class="flex items-center gap-2 text-xs">
                    <IconsCheck :isValid="passwordValidation.hasNumber" :size="16" />
                    <span class="text-sm text-gray-600">
                      {{ languageStore.t('auth.requiredChangePassword.validation.number') }}
                    </span>
                  </div>
                  <div class="flex items-center gap-2 text-xs">
                    <IconsCheck :isValid="passwordValidation.hasSpecial" :size="16" />
                    <span class="text-sm text-gray-600">
                      {{ languageStore.t('auth.requiredChangePassword.validation.special') }}
                    </span>
                  </div>
                  <div class="flex items-center gap-2 text-xs">
                    <IconsCheck :isValid="passwordValidation.hasMinLength" :size="16" />
                    <span class="text-sm text-gray-600">
                      {{ languageStore.t('auth.requiredChangePassword.validation.minLength') }}
                    </span>
                  </div>
                </div>
              </template>
            </Password>
            <small v-if="errors.newPassword" class="text-red-500">
              {{ errors.newPassword }}
            </small>
          </div>
        </Transition>
      </div>

      <Transition name="form-field" appear>
        <div style="--delay: 0.5s">
          <Button
            :label="languageStore.t('auth.requiredChangePassword.submitButton')"
            class="w-full"
            @click="handleSubmit"
            :loading="isLoading"
          />
        </div>
      </Transition>
      <Tag severity="danger" :value="errorMessage" v-if="errorMessage" class="mt-4 w-full" />
      <div class="mt-8 flex flex-col items-center justify-center gap-4">
        <img src="@/assets/logo-nepen.png" alt="logo nepen" class="h-8 w-auto" />
        <SelectLanguage />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import IconsCheck from '@/components/login/IconsCheck.vue'
import SelectLanguage from '@/components/subcomponents/SelectLanguage.vue'
import * as userService from '@/services/userService'
import { useLanguageStore } from '@/stores/LanguageStore'
import { useChangePasswordValidator } from '@/validators/changePasswordValidator'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Tag from 'primevue/tag'
import { useToast } from 'primevue/usetoast'
import { computed, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'

const languageStore = useLanguageStore()
const toast = useToast()
const router = useRouter()
const isLoading = ref(false)
const errorMessage = ref('')

const { createForm, createEmptyErrors, validateForm, validatePassword } =
  useChangePasswordValidator()

const form = reactive(createForm())
const errors = reactive(createEmptyErrors())

const passwordValidation = computed(() => validatePassword(form.newPassword))

const handleSubmit = async () => {
  const validation = validateForm(form)
  if (!validation.isValid) {
    Object.assign(errors, validation.errors)
    return
  }

  isLoading.value = true
  errorMessage.value = ''

  try {
    await userService.recoverPassword(form.email, form.expiredPassword, form.newPassword)

    toast.add({
      summary: languageStore.t('auth.requiredChangePassword.messages.successSummary'),
      detail: languageStore.t('auth.requiredChangePassword.messages.successDetail'),
      severity: 'success',
      life: 3000,
    })

    setTimeout(() => {
      router.push('/login')
    }, 1500)
  } catch (error: any) {
    const errorMsg =
      error.response?.data?.detail ||
      languageStore.t('auth.requiredChangePassword.messages.errorDetail')
    errorMessage.value = errorMsg

    toast.add({
      summary: languageStore.t('auth.requiredChangePassword.messages.errorSummary'),
      detail: errorMsg,
      severity: 'error',
      life: 3000,
    })
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.card-container {
  animation:
    cardExpand 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards,
    contentHighlight 0.8s ease-in-out 0.3s;
  transform-origin: center;
}

@keyframes cardExpand {
  0% {
    transform: scale(0.85);
    opacity: 0.7;
    max-height: 100px;
    overflow: hidden;
  }
  50% {
    transform: scale(1.02) translateY(0);
    opacity: 0.9;
  }
  100% {
    transform: scale(1) translateY(0);
    opacity: 1;
    max-height: 100%;
  }
}

@keyframes contentHighlight {
  0%,
  100% {
    box-shadow:
      0 4px 6px -1px rgba(0, 0, 0, 0.1),
      0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
  50% {
    box-shadow:
      0 20px 25px -5px rgba(14, 165, 233, 0.1),
      0 10px 10px -5px rgba(14, 165, 233, 0.05),
      0 0 0 4px rgba(14, 165, 233, 0.1);
  }
}

.form-field-enter-active {
  transition: all 0.4s ease-out;
  transition-delay: var(--delay, 0s);
}

.form-field-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.form-field-enter-to {
  opacity: 1;
  transform: translateY(0);
}
</style>
